import React, { useState, useEffect } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';
import Paper from '@mui/material/Paper';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SaveIcon from '@mui/icons-material/Save';
import RestoreIcon from '@mui/icons-material/Restore';
import { CallDetails } from '../../types/Call';
import {
  parseAIAnalysisFeedback,
  CategoryFeedback,
  CriteriaFeedback,
} from '../../utils/feedbackParser';

interface DynamicScoreEditDialogProps {
  open: boolean;
  onClose: () => void;
  callDetails: CallDetails;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onSave: (updatedScores: any, reviewNotes: string) => Promise<void>;
}

interface EditableCriteria extends CriteriaFeedback {
  originalScore: number;
  editedScore: number;
  hasChanges: boolean;
}

interface EditableCategory extends Omit<CategoryFeedback, 'criteria'> {
  criteria: EditableCriteria[];
  originalCategoryScore: number;
  newCategoryScore: number;
}

export const DynamicScoreEditDialog: React.FC<DynamicScoreEditDialogProps> = ({
  open,
  onClose,
  callDetails,
  onSave,
}) => {
  const [categories, setCategories] = useState<EditableCategory[]>([]);
  const [reviewNotes, setReviewNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [hasAnyChanges, setHasAnyChanges] = useState(false);

  // Track global feedback so we can preserve it when saving score overrides
  const [globalFeedbackRef, setGlobalFeedbackRef] = useState({ strengths: '', improvements: '' });

  // Initialize categories when dialog opens
  useEffect(() => {
    if (open) {
      const { categories: parsedCategories, globalFeedback } = parseAIAnalysisFeedback(
        callDetails.comprehensiveFeedback,
        callDetails.detailedScoresJson
      );

      // Preserve global feedback for re-inclusion on save
      setGlobalFeedbackRef(globalFeedback);

      // Filter out Review Flag category - it's non-numeric and managed by AI
      const editableCategories: EditableCategory[] = parsedCategories
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .filter((category: any) => category.category !== 'Request Human Review Flag')
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .map((category: any) => ({
          ...category,
          originalCategoryScore: category.score || 0,
          newCategoryScore: category.score || 0,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          criteria: category.criteria.map((criterion: any) => ({
            ...criterion,
            originalScore: criterion.finalScore,
            editedScore: criterion.finalScore,
            hasChanges: false,
          })),
        }));

      setCategories(editableCategories);
      setReviewNotes('');
      setError('');
      setHasAnyChanges(false);
    }
  }, [open, callDetails]);

  // Update category score when criteria scores change
  const updateCategoryScore = (categoryIndex: number) => {
    setCategories((prev) => {
      const updated = [...prev];
      const category = updated[categoryIndex];

      // Calculate new category score
      const newScore = category.criteria.reduce((sum, criterion) => sum + criterion.editedScore, 0);
      category.newCategoryScore = newScore;

      return updated;
    });
  };

  // Handle individual criterion score change
  const handleCriterionScoreChange = (
    categoryIndex: number,
    criterionIndex: number,
    newScore: number
  ) => {
    // Clamp score to valid range (0 to weight)
    const category = categories[categoryIndex];
    const criterion = category.criteria[criterionIndex];
    const clampedScore = Math.max(0, Math.min(criterion.weight, newScore));

    setCategories((prev) => {
      const updated = [...prev];
      const updatedCriterion = updated[categoryIndex].criteria[criterionIndex];

      updatedCriterion.editedScore = clampedScore;
      updatedCriterion.hasChanges = clampedScore !== updatedCriterion.originalScore;
      const originalAiScore =
        updatedCriterion.aiScore.type === 'numeric'
          ? updatedCriterion.aiScore.numericValue || 0
          : 0;
      updatedCriterion.isOverridden = clampedScore !== originalAiScore;
      updatedCriterion.manualScore = clampedScore !== originalAiScore ? clampedScore : undefined;
      updatedCriterion.finalScore = clampedScore;

      return updated;
    });

    updateCategoryScore(categoryIndex);

    // Check if any changes exist
    const hasChanges = categories.some((cat) => cat.criteria.some((crit) => crit.hasChanges));
    setHasAnyChanges(hasChanges);
  };

  // Reset a criterion to its original AI score
  const resetCriterion = (categoryIndex: number, criterionIndex: number) => {
    const criterion = categories[categoryIndex].criteria[criterionIndex];
    handleCriterionScoreChange(categoryIndex, criterionIndex, criterion.originalScore);
  };

  // Reset all scores to original
  const resetAllScores = () => {
    setCategories((prev) =>
      prev.map((category) => ({
        ...category,
        newCategoryScore: category.originalCategoryScore,
        criteria: category.criteria.map((criterion) => ({
          ...criterion,
          editedScore: criterion.originalScore,
          hasChanges: false,
          isOverridden: false,
          manualScore: undefined,
          finalScore: criterion.originalScore,
        })),
      }))
    );
    setHasAnyChanges(false);
  };

  // Calculate overall score
  const calculateOverallScore = () => {
    const totalScore = categories.reduce((sum, cat) => sum + cat.newCategoryScore, 0);
    const totalWeight = categories.reduce((sum, cat) => sum + (cat.totalWeight || 0), 0);
    return totalWeight > 0 ? (totalScore / totalWeight) * 100 : 0;
  };

  // Save changes
  const handleSave = async () => {
    setSaving(true);
    setError('');

    try {
      // Build the enhanced scores structure, preserving GlobalStrengths/GlobalImprovements
      // so the Call Review Assessment section (strengths + areas for improvement) is not lost.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const categoryScores: any = {};

      categories.forEach((category) => {
        categoryScores[category.category] = {
          categoryScore: category.newCategoryScore,
          categoryWeight: category.totalWeight,
          // The CAD marker survives a manual edit: the category is still the one scored against
          // the record, with a supervisor's scores on top of it.
          cadScoredAt: category.cadScoredAt,
          criteria: category.criteria.map((criterion) => ({
            name: criterion.name,
            weight: criterion.weight,
            aiScore: criterion.aiScore,
            manualScore: criterion.manualScore,
            finalScore: criterion.finalScore,
            isOverridden: criterion.isOverridden,
            feedback: criterion.feedback,
          })),
        };
      });

      // Re-include the Review Flag category if it existed in the original data
      const { categories: allOriginal } = parseAIAnalysisFeedback(
        callDetails.comprehensiveFeedback,
        callDetails.detailedScoresJson
      );
      const reviewFlag = allOriginal.find(
        (c) => c.category === 'Request Human Review Flag'
      );
      if (reviewFlag) {
        categoryScores['Request Human Review Flag'] = {
          categoryScore: reviewFlag.score ?? 0,
          categoryWeight: reviewFlag.totalWeight ?? 0,
          displayValue: reviewFlag.displayValue,
          criteria: reviewFlag.criteria.map((cr) => ({
            name: cr.name,
            weight: cr.weight,
            aiScore: cr.aiScore,
            manualScore: cr.manualScore,
            finalScore: cr.finalScore,
            isOverridden: cr.isOverridden,
            feedback: cr.feedback,
          })),
        };
      }

      // Wrap with GlobalStrengths/GlobalImprovements so the backend stores the full structure
      const enhancedScores = {
        GlobalStrengths: globalFeedbackRef.strengths,
        GlobalImprovements: globalFeedbackRef.improvements,
        Categories: categoryScores,
      };

      await onSave(enhancedScores, reviewNotes);
      onClose();
    } catch (_err) {
      setError(_err instanceof Error ? _err.message : 'Failed to save scores');
    } finally {
      setSaving(false);
    }
  };

  const overallScore = calculateOverallScore();

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Edit QA Scores</Typography>
          <Box display="flex" alignItems="center" gap={2}>
            <Typography variant="h6" color="primary">
              Overall: {overallScore.toFixed(1)}%
            </Typography>
            {hasAnyChanges && (
              <Button
                startIcon={<RestoreIcon />}
                onClick={resetAllScores}
                color="secondary"
                size="small"
              >
                Reset All
              </Button>
            )}
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Stack spacing={2}>
          {categories.map((category, categoryIndex) => (
            <Accordion key={category.category} defaultExpanded>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box display="flex" justifyContent="space-between" alignItems="center" width="100%">
                  <Typography variant="subtitle1" fontWeight="bold">
                    {category.displayName}
                  </Typography>
                  <Box display="flex" alignItems="center" gap={1} mr={2}>
                    <Chip
                      label={`${Math.round(category.newCategoryScore)}/${category.totalWeight} (${((category.newCategoryScore / (category.totalWeight || 1)) * 100).toFixed(1)}%)`}
                      color={
                        category.newCategoryScore !== category.originalCategoryScore
                          ? 'warning'
                          : 'default'
                      }
                      variant={
                        category.newCategoryScore !== category.originalCategoryScore
                          ? 'filled'
                          : 'outlined'
                      }
                    />
                  </Box>
                </Box>
              </AccordionSummary>

              <AccordionDetails>
                <Stack spacing={2}>
                  {category.criteria.map((criterion, criterionIndex) => (
                    <Paper key={criterionIndex} variant="outlined" sx={{ p: 2 }}>
                      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                        <Typography variant="subtitle2" fontWeight="bold" sx={{ flex: 1 }}>
                          {criterion.name}
                        </Typography>
                        <Box display="flex" alignItems="center" gap={2}>
                          <Box display="flex" alignItems="center" gap={1}>
                            <Typography variant="body2" color="text.secondary">
                              AI:{' '}
                              {criterion.aiScore.type === 'numeric'
                                ? criterion.aiScore.numericValue
                                : criterion.aiScore.textValue}
                              /{criterion.weight}
                            </Typography>
                            {criterion.hasChanges && (
                              <Button
                                size="small"
                                onClick={() => resetCriterion(categoryIndex, criterionIndex)}
                                color="secondary"
                              >
                                Reset
                              </Button>
                            )}
                          </Box>
                          <TextField
                            type="number"
                            label="Score"
                            value={criterion.editedScore}
                            onChange={(e) =>
                              handleCriterionScoreChange(
                                categoryIndex,
                                criterionIndex,
                                parseFloat(e.target.value) || 0
                              )
                            }
                            inputProps={{
                              min: 0,
                              max: criterion.weight,
                              step: 1,
                            }}
                            sx={{
                              width: 100,
                              '& .MuiOutlinedInput-root': {
                                backgroundColor: criterion.hasChanges ? 'warning.50' : 'inherit',
                              },
                            }}
                            size="small"
                          />
                          <Typography variant="body2" color="text.secondary">
                            /{criterion.weight}
                          </Typography>
                        </Box>
                      </Box>

                      {criterion.feedback && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                          {criterion.feedback}
                        </Typography>
                      )}
                    </Paper>
                  ))}
                </Stack>
              </AccordionDetails>
            </Accordion>
          ))}

          <Divider />

          <TextField
            label="Review Notes"
            value={reviewNotes}
            onChange={(e) => setReviewNotes(e.target.value)}
            fullWidth
            multiline
            minRows={3}
            maxRows={6}
            placeholder="Add notes about the score changes..."
          />
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} color="secondary">
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          color="primary"
          variant="contained"
          startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
          disabled={saving || !hasAnyChanges}
        >
          Save Changes
        </Button>
      </DialogActions>
    </Dialog>
  );
};
